export const PLAYER_COUNTRY_API_BASE_URL = 'https://api-epic.tom37-dev.fr';
export const PLAYER_COUNTRY_FALLBACK_FLAG_URL =
    `${PLAYER_COUNTRY_API_BASE_URL}/drapeaux-pays/GroupIdentity_GeoIdentity_global.png`;

const EPIC_ID_PATTERN = /^[a-f0-9]{32}$/;
const MAX_CONCURRENT_REQUESTS = 4;
const DEFAULT_FAILURE_TTL_MS = 30000;

const countryCache = new Map();
const failureCache = new Map();
const inFlightRequests = new Map();
const batchInFlightRequests = new Map();
const requestQueue = [];
let activeRequestCount = 0;

export function normalizeEpicId(value) {
    const epicId = String(value || '').trim().toLowerCase();
    return EPIC_ID_PATTERN.test(epicId) ? epicId : '';
}

function createFallbackCountry(epicId = null) {
    return {
        epicId,
        countryCode: null,
        countryName: 'Global',
        countryStatus: 'unknown',
        flagUrl: PLAYER_COUNTRY_FALLBACK_FLAG_URL,
        fallbackFlagUrl: PLAYER_COUNTRY_FALLBACK_FLAG_URL,
    };
}

function parsePlayerCountryResult(player, fallbackEpicId = null) {
    if (!player || typeof player.flag_url !== 'string' || !player.flag_url.trim()) {
        return null;
    }

    let parsedFlagUrl;
    try {
        parsedFlagUrl = new URL(player.flag_url, PLAYER_COUNTRY_API_BASE_URL);
    } catch (error) {
        return null;
    }

    if (!['http:', 'https:'].includes(parsedFlagUrl.protocol)) {
        return null;
    }

    return {
        epicId: normalizeEpicId(player.epic_id) || fallbackEpicId,
        countryCode: typeof player.country_code === 'string' ? player.country_code : null,
        countryName: typeof player.country_name === 'string' && player.country_name
            ? player.country_name
            : 'Global',
        countryStatus: typeof player.country_status === 'string'
            ? player.country_status
            : 'unknown',
        flagUrl: parsedFlagUrl.href,
        fallbackFlagUrl: PLAYER_COUNTRY_FALLBACK_FLAG_URL,
    };
}

function parsePlayerCountry(payload, fallbackEpicId = null) {
    return parsePlayerCountryResult(payload?.player, fallbackEpicId);
}

function getRetryDelayMs(response) {
    const retryAfter = response?.headers?.get?.('Retry-After');
    if (!retryAfter) return DEFAULT_FAILURE_TTL_MS;

    const seconds = Number(retryAfter);
    if (Number.isFinite(seconds) && seconds >= 0) {
        return Math.max(1000, seconds * 1000);
    }

    const retryAt = Date.parse(retryAfter);
    if (Number.isFinite(retryAt)) {
        return Math.max(1000, retryAt - Date.now());
    }

    return DEFAULT_FAILURE_TTL_MS;
}

function startQueuedRequest({ task, resolve, reject }) {
    activeRequestCount += 1;
    Promise.resolve()
        .then(task)
        .then(resolve, reject)
        .finally(() => {
            activeRequestCount -= 1;
            runNextQueuedRequests();
        });
}

function runNextQueuedRequests() {
    while (activeRequestCount < MAX_CONCURRENT_REQUESTS && requestQueue.length > 0) {
        startQueuedRequest(requestQueue.shift());
    }
}

function scheduleRequest(task) {
    return new Promise((resolve, reject) => {
        requestQueue.push({ task, resolve, reject });
        runNextQueuedRequests();
    });
}

async function requestPlayerCountry(cacheKey, url, fallbackEpicId = null) {
    const cachedCountry = countryCache.get(cacheKey);
    if (cachedCountry) return cachedCountry;

    const retryAt = failureCache.get(cacheKey) || 0;
    if (retryAt > Date.now()) {
        return createFallbackCountry(fallbackEpicId);
    }

    const inFlight = inFlightRequests.get(cacheKey);
    if (inFlight) return inFlight;

    const request = scheduleRequest(async () => {
        try {
            const response = await fetch(url);
            if (!response.ok) {
                failureCache.set(cacheKey, Date.now() + getRetryDelayMs(response));
                return createFallbackCountry(fallbackEpicId);
            }

            const country = parsePlayerCountry(await response.json(), fallbackEpicId);
            if (!country) {
                failureCache.set(cacheKey, Date.now() + DEFAULT_FAILURE_TTL_MS);
                return createFallbackCountry(fallbackEpicId);
            }

            failureCache.delete(cacheKey);
            countryCache.set(cacheKey, country);
            if (country.epicId) {
                countryCache.set(`epic:${country.epicId}`, country);
            }
            return country;
        } catch (error) {
            failureCache.set(cacheKey, Date.now() + DEFAULT_FAILURE_TTL_MS);
            return createFallbackCountry(fallbackEpicId);
        }
    }).finally(() => {
        inFlightRequests.delete(cacheKey);
    });

    inFlightRequests.set(cacheKey, request);
    return request;
}

export function fetchPlayerCountryByEpicId(value) {
    const epicId = normalizeEpicId(value);
    if (!epicId) return Promise.resolve(createFallbackCountry());

    return requestPlayerCountry(
        `epic:${epicId}`,
        `${PLAYER_COUNTRY_API_BASE_URL}/api/player-countries/${epicId}`,
        epicId
    );
}

export function resolvePlayerCountryByQuery(value) {
    const query = String(value || '').trim();
    if (!query) return Promise.resolve(createFallbackCountry());

    return requestPlayerCountry(
        `query:${query.toLowerCase()}`,
        `${PLAYER_COUNTRY_API_BASE_URL}/api/player-countries/resolve?query=${encodeURIComponent(query)}`
    );
}

async function fetchPlayerCountriesBatch(epicIds) {
    const batchKey = [...epicIds].sort().join(',');
    const inFlight = batchInFlightRequests.get(batchKey);
    if (inFlight) return inFlight;

    const request = (async () => {
        const response = await fetch(`${PLAYER_COUNTRY_API_BASE_URL}/api/find-countries`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({ epic_ids: epicIds.join('\n') }).toString(),
        });
        if (!response.ok) return {};

        const payload = await response.json();
        const countries = {};
        (Array.isArray(payload?.results) ? payload.results : []).forEach((result) => {
            const epicId = normalizeEpicId(result?.epic_id);
            const country = parsePlayerCountryResult(result, epicId || null);
            if (!epicId || !country) return;

            countries[epicId] = country;
            countryCache.set(`epic:${epicId}`, country);
        });
        return countries;
    })()
        .catch(() => ({}))
        .finally(() => {
            batchInFlightRequests.delete(batchKey);
        });

    batchInFlightRequests.set(batchKey, request);
    return request;
}

export async function loadPlayerCountriesByEpicId(values) {
    const epicIds = [...new Set((values || []).map(normalizeEpicId).filter(Boolean))];
    const countries = {};
    const missingEpicIds = [];

    epicIds.forEach((epicId) => {
        const cachedCountry = countryCache.get(`epic:${epicId}`);
        if (cachedCountry) {
            countries[epicId] = cachedCountry;
        } else {
            missingEpicIds.push(epicId);
        }
    });

    if (missingEpicIds.length > 0) {
        Object.assign(countries, await fetchPlayerCountriesBatch(missingEpicIds));
    }

    epicIds.forEach((epicId) => {
        if (!countries[epicId]) {
            countries[epicId] = createFallbackCountry(epicId);
        }
    });
    return countries;
}

export async function loadPlayerCountriesByQuery(values) {
    const queries = (values || []).map((value) => String(value || '').trim()).filter(Boolean);
    const uniqueQueries = new Map();
    queries.forEach((query) => {
        const key = query.toLowerCase();
        if (!uniqueQueries.has(key)) uniqueQueries.set(key, query);
    });

    const countriesByKey = new Map(await Promise.all(
        [...uniqueQueries.entries()].map(async ([key, query]) => [
            key,
            await resolvePlayerCountryByQuery(query),
        ])
    ));

    return Object.fromEntries(
        queries.map((query) => [query, countriesByKey.get(query.toLowerCase())])
    );
}

export function __resetPlayerCountryApiForTests() {
    countryCache.clear();
    failureCache.clear();
    inFlightRequests.clear();
    batchInFlightRequests.clear();
    requestQueue.splice(0, requestQueue.length);
    activeRequestCount = 0;
}
